<template>
  <div class="register-bank">
    <v-alert
      v-if="productsWithoutKeywords.length"
      type="warning"
      dense
      prominent
      class="mb-3 keywords-missing-alert"
      @click.native="openGenerateKeywordsDialog"
    >
      <div class="d-flex align-center">
        <div>
          {{ productsWithoutKeywords.length }} produto{{
            productsWithoutKeywords.length > 1 ? "s" : ""
          }}
          sem palavras-chave. Clique para gerar agora.
        </div>
        <v-spacer />
        <v-btn small color="warning" dark depressed class="ml-2"> Gerar </v-btn>
      </div>
    </v-alert>

    <div class="d-flex justify-space-between align-center mb-2 gap-4">
      <v-text-field
        v-model="search"
        label="Pesquisar"
        outlined
        hide-details
        dense
      ></v-text-field>
      <v-btn color="primary" small @click="openMerchProductDialog(null)">
        <v-icon left small> mdi-plus </v-icon>
        Produto
      </v-btn>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" />
    <v-alert v-if="error" type="error" dismissible @input="clearError">
      {{ error }}
    </v-alert>

    <v-chip-group v-model="filterType" mandatory color="primary">
      <v-chip filter small :value="false">Todos</v-chip>
      <v-chip filter small value="PRODUCT">Produto</v-chip>
      <v-chip filter small value="COMBO">Combo</v-chip>
    </v-chip-group>

    <p
      v-if="filteredMerchProducts.length === 0"
      class="text-caption text--secondary mb-0 text-center"
    >
      Nenhum produto encontrado
    </p>
    <p v-else class="text-caption text--secondary mb-0 text-center">
      {{ filteredMerchProducts.length }} produto{{
        filteredMerchProducts.length > 1 ? "s" : ""
      }}
      encontrado{{ filteredMerchProducts.length > 1 ? "s" : "" }}
    </p>

    <div ref="productScroller">
      <RecycleScroller
        v-if="filteredMerchProducts.length"
        class="scroller"
        page-mode
        :items="filteredMerchProducts"
        :item-size="itemSize"
        :item-secondary-size="itemSecondarySize"
        :grid-items="columns"
        :buffer="itemSize * 6"
        key-field="id"
      >
        <template #default="{ item, active }">
          <MerchProductCard
            v-if="active"
            class="ma-1"
            :merchProduct="item"
            @edit="openMerchProductDialog"
          />
        </template>
      </RecycleScroller>
    </div>

    <MerchProductDialog
      @updated="updateMerchProduct"
      @created="createMerchProduct"
    />
    <GenerateKeywordsDialog
      ref="generateKeywordsDialog"
      @updated="updateMerchProduct"
    />
  </div>
</template>

<script>
import MerchProductDialog from "@/components/admin/merchProducts/MerchProductDialog.vue";
import MerchProductCard from "@/components/admin/merchProducts/MerchProductCard.vue";
import GenerateKeywordsDialog from "@/components/admin/merchProducts/GenerateKeywordsDialog.vue";
import merchProductsService from "@/services/admin/merchProducts";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import { RecycleScroller } from "vue-virtual-scroller";

export default {
  name: "MerchProducts",
  components: {
    MerchProductDialog,
    MerchProductCard,
    GenerateKeywordsDialog,
    RecycleScroller,
  },
  data() {
    return {
      error: null,
      loading: false,
      search: "",
      merchProducts: [],
      filterType: false,
      scrollerWidth: 0,
    };
  },
  computed: {
    filteredMerchProducts() {
      return this.merchProducts
        .filter((merchProduct) => {
          if (this.filterType && merchProduct.type !== this.filterType)
            return false;

          const lowerSearch = this.search.toLowerCase();
          if (!lowerSearch) return true;
          if (merchProduct.name.toLowerCase().includes(lowerSearch))
            return true;
          if (
            (merchProduct.description || "").toLowerCase().includes(lowerSearch)
          )
            return true;
          return (merchProduct.keywords || []).some((keyword) =>
            String(keyword).toLowerCase().includes(lowerSearch)
          );
        })
        .sort((a, b) => {
          if (a.name !== b.name) return a.name.localeCompare(b.name);
          return a.description.localeCompare(b.description);
        });
    },
    columns() {
      if (this.$vuetify.breakpoint.lgAndUp) return 6;
      if (this.$vuetify.breakpoint.mdAndUp) return 4;
      if (this.$vuetify.breakpoint.smAndUp) return 3;
      return 2;
    },
    itemSecondarySize() {
      const colWidth = this.scrollerWidth / this.columns;
      if (!colWidth || !isFinite(colWidth)) return 300;
      return colWidth;
    },
    itemSize() {
      return this.itemSecondarySize + 56;
    },
    productsWithoutKeywords() {
      return this.merchProducts.filter(
        (merchProduct) =>
          !Array.isArray(merchProduct.keywords) ||
          merchProduct.keywords.length === 0
      );
    },
  },
  methods: {
    openMerchProductDialog(merchProduct = null) {
      this.$emit("open-merch-product-dialog", merchProduct);
    },
    openGenerateKeywordsDialog() {
      this.$refs.generateKeywordsDialog.open(this.productsWithoutKeywords);
    },
    async fetchMerchProducts() {
      this.loading = true;
      try {
        const response = await merchProductsService.getAllMerchProducts();
        this.merchProducts = response.merchProducts;
      } catch (error) {
        this.error =
          error.message ||
          "Erro ao carregar bancos. Por favor, tente novamente.";
      } finally {
        this.loading = false;
      }
    },
    updateMerchProduct(updatedMerchProduct) {
      const index = this.merchProducts.findIndex(
        (m) => m.id === updatedMerchProduct.id
      );
      if (index !== -1)
        this.$set(this.merchProducts, index, updatedMerchProduct);
    },
    createMerchProduct(createdMerchProduct) {
      this.merchProducts.push(createdMerchProduct);
    },
    updateWidth() {
      const el = this.$refs.productScroller;
      if (el) this.scrollerWidth = el.offsetWidth;
    },
  },
  watch: {
    loading() {
      this.updateWidth();
    },
    columns() {
      this.updateWidth();
    },
  },
  mounted() {
    this.fetchMerchProducts();
    this.updateWidth();
    window.addEventListener("resize", this.updateWidth);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateWidth);
  },
};
</script>

<style scoped>
.keywords-missing-alert {
  cursor: pointer;
}
</style>
